import { FC, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { FullScreenModal } from 'src/Components/FullScreenModal'
import { createdFirst, feedback } from 'src/Router/routes'
import SuccessIcon from 'src/Assets/Svg/pages-icons/Success.svg?react'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { GreyTextButton } from 'src/Components/Buttons/GreyTextButton'
import { useUpdateArtistFeatureFlagsMutation } from 'src/Redux/A-BFeaturesApi'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { REELS_FEATURE_VALUE } from 'src/Hooks/useReels'

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  max-width: 358px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    max-width: 600px;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`

const Text = styled(Title)`
  font-size: 14px;
  font-weight: 400;
`

const StyledSuccessIcon = styled(SuccessIcon)`
  color: ${({ theme }) => theme.color.brandGreen};
`

const StyledGreyTextButton = styled(GreyTextButton)`
  padding-left: 40px;
  padding-right: 40px;
`

export const SuccessCreatedFirst: FC = () => {
  const navigate = useNavigate()
  const [addFeatureForUser] = useUpdateArtistFeatureFlagsMutation()
  const { search } = useCustomSearchParams()
  const hasReels = search.get('reels') === 'true'
  const { campaignType } = useParams<{
    campaignType: CampaignPlatformTypeEnum
  }>()
  const firstParagraph =
    'It will be reviewed by our A&R team and we`ll get back to you within 15-45 min.'
  const secondParagraph =
    'We will notify you on email and SMS if your campaign is accepted.'

  const handleClick = (): void => {
    if (campaignType) {
      navigate(`${createdFirst}/${campaignType}${feedback}`)
    }
  }

  const handleGoToFeedback = (): void => {
    navigate(`${createdFirst}/${CampaignPlatformTypeEnum.META}${feedback}`)
  }

  useEffect(() => {
    if (campaignType === 'tiktok' && hasReels) {
      addFeatureForUser({
        featureName: REELS_FEATURE_VALUE,
        variation: 1,
      })
    }
  }, [addFeatureForUser, campaignType, hasReels])

  const isMetaAds = campaignType === CampaignPlatformTypeEnum.META

  return (
    <FullScreenModal open>
      <Wrapper>
        <Container>
          <StyledSuccessIcon width={40} height={40} />
          {isMetaAds ? (
            <>
              <TextContainer>
                <Title>Your campaign is ready! 🎉</Title>
                <Text>
                  You can now proceed to schedule your campaign and start
                  reaching new listeners immediately.
                </Text>
              </TextContainer>
              <div>
                <div>
                  <StyledGreyTextButton onClick={handleGoToFeedback}>
                    Continue
                  </StyledGreyTextButton>
                </div>
              </div>
            </>
          ) : (
            <>
              <TextContainer>
                <Title>You've submitted your first track 🎉</Title>
                <div>
                  <Text>{firstParagraph}</Text>
                  <Text>{secondParagraph}</Text>
                </div>
              </TextContainer>

              <div>
                <div>
                  <StyledGreyTextButton onClick={handleClick}>
                    Continue
                  </StyledGreyTextButton>
                </div>
              </div>
            </>
          )}
        </Container>
      </Wrapper>
    </FullScreenModal>
  )
}
